/**
 * Shared formatters/icon helpers for monitoring execution data.
 *
 * The duration + timestamp formatters now delegate to the canonical
 * `utils/dateTime` module; this file keeps the JSX-bearing
 * `executionStatusIcon` helper and a thin, backward-compatible
 * re-export of the two formatters so existing imports keep working.
 */

import {
  CheckmarkCircleFilled,
  DismissCircleFilled,
  CircleFilled,
  WarningFilled,
} from '@fluentui/react-icons';
import type { NodeExecutionStatus } from '@/types/monitoring';
import { formatDateTime, formatDuration } from '@/utils/dateTime';

/**
 * Returns the Fluent icon element for a given execution status, with the
 * caller-supplied className so the color/sizing can be themed in context.
 *
 * Non-terminal statuses (running, skipped, pending, …) fall back to a
 * neutral `CircleFilled` so the icon column stays visually anchored.
 */
export const executionStatusIcon = (status: NodeExecutionStatus, className: string) => {
  if (status === 'succeeded') return <CheckmarkCircleFilled className={className} fontSize={14} />;
  if (status === 'failed') return <DismissCircleFilled className={className} fontSize={14} />;
  if (status === 'cancelled') return <WarningFilled className={className} fontSize={14} />;
  return <CircleFilled className={className} fontSize={14} />;
};

/**
 * Formats a millisecond duration for execution surfaces. Thin wrapper
 * around `formatDuration` using the `'execution'` style (em-dash
 * fallback, always emits the `Ns` slot once we're in `Nm Ns` territory).
 */
export const formatExecutionDuration = (ms?: number | null): string =>
  formatDuration(ms, { style: 'execution' });

/**
 * Formats an ISO timestamp using the user's locale. Thin wrapper around
 * `formatDateTime` using the `'locale'` style with an em-dash fallback.
 */
export const formatExecutionTimestamp = (iso?: string | null): string =>
  formatDateTime(iso, 'locale');
